// src/lib/contractFxDate.js — the shortcuts offered for „which day's exchange rate".
// PURE, tested by contractFxDate.test.mjs.
//
// Owner, 2026-09-13: „add a real payment-date field where conversion depends on it… quick-select
// Agreement date · Payment date · Today · Choose date. Do not manufacture a payment date if none
// exists."
//
// 🔑 THE LAST SENTENCE IS THE WHOLE DESIGN. Three of the four shortcuts always have an answer;
// „payment date" has one only if somebody recorded it. So the option is OFFERED ONLY WHEN THE
// VALUE EXISTS — never greyed out with a tooltip, and never silently resolving to the agreement
// date, which is the substitution 1173 removed from the server for the same reason. A shortcut
// that quietly fills in the wrong day is worse than one that is not there: the agent reads the
// date back and it looks like theirs.
//
// „Choose date" is not in this list — it is the plain date input the shortcuts sit beside, which
// is always available and is what the agent falls back to.

const ISO = /^\d{4}-\d{2}-\d{2}$/
const iso = v => (typeof v === 'string' && ISO.test(v) ? v : null)

/** Today in the LOCAL calendar. ⚠️ `toISOString()` is UTC and lands on yesterday for a Tbilisi
 *  evening (UTC+4), which would put a rate date one day early on every contract drawn after 20:00. */
export function todayISO(now = new Date()) {
  const p = n => String(n).padStart(2, '0')
  return `${now.getFullYear()}-${p(now.getMonth() + 1)}-${p(now.getDate())}`
}

/**
 * @param {object} values the contract's values
 * @param {Date}   now
 * @returns {Array<{id, date}>} the shortcuts that have a real answer, in the owner's order
 */
export function fxDateChoices(values = {}, now = new Date()) {
  const out = []
  const agreement = iso(values.agreement_date)
  if (agreement) out.push({ id: 'agreement', date: agreement })
  // The advance is the only document that converts, so `advance_paid_on` is the payment date
  // here; `binder_paid_on` is read too so a future converting document inherits the shortcut
  // rather than needing this list edited.
  const paid = iso(values.advance_paid_on) || iso(values.binder_paid_on)
  if (paid) out.push({ id: 'payment', date: paid })
  out.push({ id: 'today', date: todayISO(now) })
  return out
}

/** Which shortcut, if any, the current value corresponds to — so the row can show what is chosen
 *  without storing a second piece of state that can disagree with the date itself. */
export function activeFxChoice(current, values = {}, now = new Date()) {
  const c = iso(current)
  if (!c) return null
  const hit = fxDateChoices(values, now).find(x => x.date === c)
  return hit ? hit.id : null
}
